import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Geometry, LineString } from "ol/geom";
import { Stroke, Style } from "ol/style";
import {
    RouteGeometry,
    VoyageRoute,
    VoyageRoutingModel, VoyageRoutingModelBase,
} from '../models/voyage-routing-model'
import { Feature } from "ol";
import * as turf from "@turf/turf";
import { transform } from "ol/proj";
import { Coordinate } from "ol/coordinate";
import { PlanningLayersColors } from "./types/plan-layer.types";
import { CreateDateLineAndGreatCircleFeatures, isGreatCircleLeg, offsetLineCoordinates } from '../utils/map-utils'
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types'

const _PROJECTION = (<any>window)["appenv"].projection;
const nmToKm = 1.852;

export class StarRouteDeviationLayer extends EventTarget {
    private _layer: VectorLayer<VectorSource<Geometry>>| null = null;
    private _selectedStarRouteId: string = '';
    private _selectedRouteId: string = '';
    private _selectedScheduleId: string = '';
    private _hasExplicitStarRouteSelection: boolean = false;
    private _adoptedRoutesVectorSource: VectorSource<Geometry> | null = null;
    private _colors: string[] = [];

    constructor(colors?: []) {
        super();
        this.setColors(colors || []);
        this._onStarRouteChanged = this._onStarRouteChanged.bind(this);
    }

    public setRoutes(starRouteId: string, routeId: string, fallbackStarRouteId?: string): void {
        this._hasExplicitStarRouteSelection = !!starRouteId;
        this._selectedStarRouteId = starRouteId || fallbackStarRouteId || VoyageRoutingModel?.routes.find(route =>
            route.name?.replace(/\s+/g, '').toLowerCase() === 'starroute1'
        )?.routeId || VoyageRoutingModel?.routes.find(route => route.routeCreatedBy === 'system')?.routeId || '';
        this._selectedRouteId = routeId;

        this._onStarRouteChanged();
    }
    public setScheduleId(scheduleId: string) {
        if (scheduleId !== this._selectedScheduleId) {
            this.clearRoutes();
        }
        this._selectedScheduleId = scheduleId;
    }

    get layer(): VectorLayer<VectorSource<Geometry>> | null {
        return this._layer;
    }

    async initialize(): Promise<void> {
        this._adoptedRoutesVectorSource = new VectorSource();
        this._layer= new VectorLayer({ source: this._adoptedRoutesVectorSource, visible: true });
        VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.StarRoutesChanged, this._onStarRouteChanged);
    }

    setColors(colors: string[]): void {
        this._colors = colors || this._colors;
    }

    clearRoutes() {
        this._adoptedRoutesVectorSource?.clear();
    }

    private _onStarRouteChanged() {
        this.clearRoutes();
        const allRoutes = VoyageRoutingModel!.routes;

        // Ensure only the routes with selectedRouteId or routeId are shown, others hidden
        if (this._hasExplicitStarRouteSelection) {
            allRoutes.forEach(route => {
                const shouldShowRoute = route.routeId === this._selectedStarRouteId || route.routeId === this._selectedRouteId;
                VoyageRoutingModel!._setRouteViewStatus(route.routeId, this._selectedScheduleId, shouldShowRoute);
            });
        }


        const selectedStarRoute = VoyageRoutingModel?.routes.find(route =>
            route.routeId === this._selectedStarRouteId
        );

        if (selectedStarRoute) {
            const selectedStarRouteIndex = VoyageRoutingModel?.routes.findIndex(route =>
                route.routeId === this._selectedStarRouteId
            ) ?? 0;
            this._plotAdoptedAreas(selectedStarRoute, selectedStarRouteIndex);
        }
    }

    //only LineStrings are chosen because they directly represent the paths or routes that need to be visualized and processed
    private _extractLineStrings(route_geometry: RouteGeometry) {
        const features: Feature<Geometry>[] = [];

        for (let idx = 0; idx < route_geometry.length; idx++) {
            const f = route_geometry[idx];
            if (f.geometry.type !== "LineString") continue;

            const lineString = new Feature({
                geometry: new LineString(f.geometry.coordinates),
            });
            const legType = f.properties.leg_type ?? 'rhumbline';
            lineString.set('leg_type', legType);

            // Resolve max_x_track: prefer LineString's own value, fall back to preceding waypoint
            let xtd = f.properties.max_x_track;
            if (xtd == null && idx > 0) {
                const prevWp = route_geometry[idx - 1];
                if (prevWp.geometry.type === "Point") {
                    xtd = prevWp.properties.max_x_track ?? prevWp.properties.userInput?.max_x_track;
                }
            }

            const feature = CreateDateLineAndGreatCircleFeatures(lineString);
            feature.forEach((feat) => {
                feat.setProperties({
                    max_x_track: xtd,
                    leg_type: legType
                });
            });

            features.push(...feature);
        }

        return features;
    }

    private _adjustCoordinatesForMeridianCrossing(coordinates: turf.helpers.Position[]) {
        // Loop through the coordinates (except the first and last)
        for (let i = 1; i < coordinates.length - 1; i++) {
            const current = coordinates[i];
            const previous = coordinates[i - 1];

            // Check if there is a big longitude jump (crossing the 180th meridian)
            const longitudeDifference = current[0] - previous[0];

            // Adjust the longitude to prevent wrapping issues
            if (longitudeDifference > 180) {
                current[0] -= 360;  // Fix eastward crossing (+180° to -180°)
            } else if (longitudeDifference < -180) {
                current[0] += 360;  // Fix westward crossing (-180° to +180°)
            }
        }

        return coordinates;
    }

    // Returns two parallel offset lines (port and starboard) per segment, without end caps.
    private _createOffsetLines(features: Feature<Geometry>[], nmToKm: number): turf.helpers.Feature<turf.helpers.LineString>[] {
        const offsetLines: turf.helpers.Feature<turf.helpers.LineString>[] = [];

        features.forEach((line) => {
            const legType = line.getProperties().leg_type;

            // StarRoute deviation boundaries use the policy limit for each leg type,
            // rather than the route's navigation XTD value.
            const max_x_track = isGreatCircleLeg(legType) ? 50 : 10;

            const convertToKm = max_x_track * nmToKm;
            if (convertToKm <= 0) return;

            const coordinates = (<LineString>line.getGeometry()).getCoordinates();
            const greatCircle = isGreatCircleLeg(legType);

            // Offset each vertex geodesically so the requested distance is preserved at every latitude.
            const starboardLine = turf.lineString(offsetLineCoordinates(coordinates, convertToKm, 1, greatCircle));
            const portLine = turf.lineString(offsetLineCoordinates(coordinates, convertToKm, -1, greatCircle));

            this._adjustCoordinatesForMeridianCrossing(starboardLine.geometry.coordinates);
            this._adjustCoordinatesForMeridianCrossing(portLine.geometry.coordinates);

            offsetLines.push(starboardLine, portLine);
        });

        return offsetLines;
    }

    private _createStyledLineFeature(coordinates: Coordinate[], uiRouteColor: string): Feature {
        const lineFeature = new Feature(new LineString(coordinates));

        lineFeature.setStyle(
            new Style({
                stroke: new Stroke({
                    color: uiRouteColor,
                    lineDash: [2, 10, 15, 10],
                    width: 3,
                }),
            })
        );
        return lineFeature;
    }

    private _transformCoordinates(coordsArray: any[], projection: string): Coordinate[] {
        return coordsArray
            .map(innerArray => {
                if (Array.isArray(innerArray) && innerArray.length === 2) {
                    const coordinate: Coordinate = [innerArray[0], innerArray[1]];
                    return transform(coordinate, "EPSG:4326", projection);
                }
                return undefined;
            })
            .filter((coord): coord is Coordinate => coord !== undefined);
    }

    private _plotAdoptedAreas(route: VoyageRoute, uiRouteIndex: number) {
        if (!route.route_info) {
            return;
        }

        const features = this._extractLineStrings(route.route_geometry);
        const offsetLines = this._createOffsetLines(features, nmToKm);
        const uiRouteColor = this._colors[uiRouteIndex];

        // Offset lines come in pairs: [starboard0, port0, starboard1, port1, ...]
        for (let i = 0; i < offsetLines.length; i += 2) {
            const starboardCoords = this._transformCoordinates(offsetLines[i].geometry.coordinates, _PROJECTION);
            const portCoords = this._transformCoordinates(offsetLines[i + 1].geometry.coordinates, _PROJECTION);

            // Draw the two parallel dashed lines
            this._adoptedRoutesVectorSource?.addFeature(this._createStyledLineFeature(starboardCoords, uiRouteColor));
            this._adoptedRoutesVectorSource?.addFeature(this._createStyledLineFeature(portCoords, uiRouteColor));

            // Join parallel lines with straight lines at both ends of the segment
            if (starboardCoords.length > 0 && portCoords.length > 0) {
                this._adoptedRoutesVectorSource?.addFeature(
                    this._createStyledLineFeature([starboardCoords[0], portCoords[0]], uiRouteColor)
                );
                this._adoptedRoutesVectorSource?.addFeature(
                    this._createStyledLineFeature(
                        [starboardCoords[starboardCoords.length - 1], portCoords[portCoords.length - 1]],
                        uiRouteColor
                    )
                );
            }
        }
    }
}